import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useSlugNavigate } from '../hooks/useNavigation'
import DashboardLayout from '../components/DashboardLayout'
import FileUploader from '../components/migrations/FileUploader'
import { getMigration, getMigrationConfig, updateMigration, listMigrationFiles, deleteMigrationFile, getFileDownloadUrl } from '../api/migrations'
import { SOURCE_SYSTEMS, formatOdooVersion } from '../components/migrations/MigrationForm'

const MigrationDetailPage = () => {
  const { migrationId } = useParams()
  const { getActiveSlug } = useAuth()
  const navigate = useSlugNavigate()
  const slug = getActiveSlug()

  const [migration, setMigration] = useState(null)
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showFileUploader, setShowFileUploader] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState(null)
  const [saving, setSaving] = useState(false)

  // Configuration state (read-only from API)
  const [config, setConfig] = useState(null)

  useEffect(() => {
    loadMigration()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [migrationId])

  const loadMigration = async () => {
    try {
      setLoading(true)
      const [migrationData, filesData] = await Promise.all([
        getMigration(migrationId),
        listMigrationFiles(migrationId).catch(() => [])
      ])
      setMigration(migrationData)
      setFiles(filesData)

      // Load existing config from API
      try {
        const configData = await getMigrationConfig(migrationId)
        setConfig(configData)
      } catch {
        // No config exists yet
        setConfig(null)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const getSourceSystem = (id) => SOURCE_SYSTEMS.find(s => s.id === id)

  const getStatusConfig = (status) => {
    const configs = {
      DRAFT: { label: 'Draft', color: 'gray', icon: 'edit_note' },
      AWAITING_FILES: { label: 'Awaiting Files', color: 'amber', icon: 'hourglass_empty' },
      FILES_UPLOADED: { label: 'Files Uploaded', color: 'blue', icon: 'upload_file' },
      IN_PROGRESS: { label: 'In Progress', color: 'blue', icon: 'sync' },
      REVIEW: { label: 'Under Review', color: 'purple', icon: 'rate_review' },
      COMPLETED: { label: 'Completed', color: 'green', icon: 'check_circle' },
      FAILED: { label: 'Failed', color: 'red', icon: 'error' }
    }
    return configs[status] || configs.DRAFT
  }

  // File upload handlers
  const handleFileUploadComplete = () => {
    // Reload migration to get updated file list
    loadMigration()
    setShowFileUploader(false)
  }

  const handleFileUploadError = (errors) => {
    console.error('Upload errors:', errors)
    alert('Some files failed to upload. Please try again.')
  }

  const handleDownloadFile = async (file) => {
    try {
      const { url } = await getFileDownloadUrl(migrationId, file.id)
      window.open(url, '_blank')
    } catch (err) {
      alert('Failed to download file: ' + err.message)
    }
  }

  const handleDeleteFile = async (file) => {
    if (!confirm(`Are you sure you want to delete "${file.file_name}"?`)) {
      return
    }

    try {
      setDeletingFileId(file.id)
      await deleteMigrationFile(migrationId, file.id)
      // Update local state
      setFiles(files.filter(f => f.id !== file.id))
    } catch (err) {
      alert('Failed to delete file: ' + err.message)
    } finally {
      setDeletingFileId(null)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
  }

  const getFileTypeLabel = (type) => {
    const labels = {
      SOURCE_DATA: 'Source Data',
      CONFIG: 'Config',
      ATTACHMENT: 'Attachment',
      LOG: 'Log',
      REPORT: 'Report',
      OTHER: 'Other'
    }
    return labels[type] || type
  }

  const handleFinalizeSubmission = async () => {
    setSaving(true)
    try {
      await updateMigration(migrationId, { status: 'FILES_UPLOADED' })
      navigate('/migrations')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">Loading migration...</div>
        </div>
      </DashboardLayout>
    )
  }

  if (error || !migration) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">error</span>
            <p className="text-gray-500">{error || 'Migration not found'}</p>
            <Link to={`/${slug}/migrations`} className="text-primary hover:underline mt-2 inline-block">
              Back to Migrations
            </Link>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const sourceSystem = getSourceSystem(migration.source_system)
  const statusConfig = getStatusConfig(migration.status)
  const hasFiles = files.length > 0

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Link to={`/${slug}/migrations`} className="hover:text-primary transition-colors">
            Migrations
          </Link>
          <span className="material-symbols-outlined text-sm">chevron_right</span>
          <span className="text-[#111418]">{migration.name}</span>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#111418] mb-1">Migration Details</h1>
            <p className="text-sm text-gray-500">
              <span className="material-symbols-outlined text-sm align-text-bottom mr-1">info</span>
              Migration ID: #{migration.id?.slice(0, 8).toUpperCase()} — {sourceSystem?.name || migration.source_system} to Odoo{config?.odoo_version ? ` ${formatOdooVersion(config.odoo_version)}` : ''}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
            <span className="material-symbols-outlined text-sm">{statusConfig.icon}</span>
            {statusConfig.label}
          </span>
        </div>

        {/* Migration Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Source System</p>
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <span className="material-symbols-outlined text-gray-600">
                  {sourceSystem?.icon || 'description'}
                </span>
              </div>
              <p className="text-sm font-bold text-[#111418]">{sourceSystem?.name || migration.source_system}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Destination</p>
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-green-100 flex items-center justify-center">
                <span className="material-symbols-outlined text-green-600">inventory_2</span>
              </div>
              <div>
                <p className="text-sm font-bold text-[#111418]">Odoo</p>
                {config?.odoo_version && (
                  <p className="text-xs text-gray-500">Version {formatOdooVersion(config.odoo_version)}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Status</p>
            <div className="flex items-center gap-3">
              <div className={`size-10 rounded-lg bg-${statusConfig.color}-100 flex items-center justify-center`}>
                <span className={`material-symbols-outlined text-${statusConfig.color}-600`}>{statusConfig.icon}</span>
              </div>
              <p className="text-sm font-bold text-[#111418]">{statusConfig.label}</p>
            </div>
          </div>
        </div>

        {/* Configuration Section - Read-only Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary">settings</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#111418]">Odoo Connection</h2>
                <p className="text-sm text-gray-500">Destination Odoo instance configuration</p>
              </div>
            </div>
          </div>

          {config && config.has_encrypted_credentials ? (
            <>
              {/* Saved Configuration Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="material-symbols-outlined text-green-600">check_circle</span>
                  <span className="font-bold text-green-800">Odoo Connection Configured</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">URL</p>
                    <p className="font-medium text-[#111418]">{config.odoo_url || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Version</p>
                    <p className="font-medium text-[#111418]">{config.odoo_version || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Company</p>
                    <p className="font-medium text-[#111418]">{config.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Credentials</p>
                    <p className="font-medium text-green-600 flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">lock</span>
                      Securely stored
                    </p>
                  </div>
                </div>
              </div>

              {/* Migration Options - Read-only */}
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-bold text-[#111418] mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-lg">tune</span>
                  Migration Options
                </h3>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-3">
                    <span className={`material-symbols-outlined text-lg ${config.rewrite_doc_number ? 'text-green-600' : 'text-gray-400'}`}>
                      {config.rewrite_doc_number ? 'check_box' : 'check_box_outline_blank'}
                    </span>
                    <div>
                      <span className="text-sm text-[#111418]">Rewrite document numbers</span>
                      <p className="text-xs text-gray-400">Regenerate document numbers to match Odoo&apos;s sequence</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* No configuration saved */
            <div className="text-center py-8 border-2 border-dashed border-amber-200 rounded-xl bg-amber-50">
              <span className="material-symbols-outlined text-4xl text-amber-400 mb-2">warning</span>
              <p className="text-sm text-amber-700 font-medium mb-1">No Odoo connection configured</p>
              <p className="text-xs text-amber-600">
                This migration was created without Odoo credentials. Please contact support if you need to update the configuration.
              </p>
            </div>
          )}
        </div>

        {/* File Upload Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-[#111418]">Migration Files</h2>
              <p className="text-sm text-gray-600">
                Upload source data exports, configuration files, or attachments (ZIP and PDF up to 4GB).
              </p>
            </div>
            {!showFileUploader && (
              <button
                type="button"
                onClick={() => setShowFileUploader(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white font-bold text-sm rounded-lg hover:bg-primary/90 transition-all"
              >
                <span className="material-symbols-outlined text-lg">cloud_upload</span>
                Upload Files
              </button>
            )}
          </div>

          {/* Uppy File Uploader */}
          {showFileUploader && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-600">
                  Drag and drop files or click to browse. Large files are uploaded in chunks with resume support.
                </p>
                <button
                  type="button"
                  onClick={() => setShowFileUploader(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
              <FileUploader
                migrationId={migrationId}
                onUploadComplete={handleFileUploadComplete}
                onUploadError={handleFileUploadError}
              />
            </div>
          )}

          {/* Uploaded Files List */}
          {files.length > 0 ? (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Uploaded Files ({files.length})</p>
              </div>
              <div className="divide-y divide-gray-100">
                {files.map(file => (
                  <div key={file.id} className="px-4 py-3 flex items-center gap-3">
                    <div className="size-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <span className="material-symbols-outlined text-orange-600">description</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#111418] truncate">{file.file_name}</p>
                      <p className="text-xs text-gray-500">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700 mr-2">
                          {getFileTypeLabel(file.file_type)}
                        </span>
                        {formatFileSize(file.file_size)}
                        {file.description && (
                          <span className="ml-2 text-gray-400">- {file.description}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDownloadFile(file)}
                        className="p-1.5 text-gray-400 hover:text-primary rounded hover:bg-primary/10 transition-colors"
                        title="Download"
                      >
                        <span className="material-symbols-outlined text-lg">download</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteFile(file)}
                        disabled={deletingFileId === file.id}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {deletingFileId === file.id ? (
                          <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                        ) : (
                          <span className="material-symbols-outlined text-lg">delete</span>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : !showFileUploader && (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">cloud_upload</span>
              <p className="text-sm text-gray-500 mb-3">No files uploaded yet</p>
              <button
                type="button"
                onClick={() => setShowFileUploader(true)}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Upload your first file
              </button>
            </div>
          )}
        </div>

        {/* Secure Transfer Notice */}
        <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100 mb-8">
          <span className="material-symbols-outlined text-primary">verified_user</span>
          <div>
            <p className="text-sm font-bold text-[#111418]">Secure Transfer</p>
            <p className="text-xs text-gray-600">Your data is encrypted using 256-bit SSL during transfer.</p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <Link
            to={`/${slug}/migrations`}
            className="px-6 h-10 inline-flex items-center border border-gray-200 rounded-lg text-[#111418] font-bold text-sm hover:bg-gray-50 transition-all"
          >
            Back to Migrations
          </Link>

          <button
            type="button"
            onClick={handleFinalizeSubmission}
            disabled={saving || !hasFiles}
            className="flex items-center gap-2 px-6 h-10 bg-primary text-white font-bold text-sm rounded-lg shadow-md shadow-primary/20 hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Finalize Submission'}
            {!saving && <span className="material-symbols-outlined text-sm">arrow_forward</span>}
          </button>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default MigrationDetailPage
